<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { ChevronUp, ChevronDown, TriangleAlert } from 'lucide-svelte';
  import { quotationsApi } from '$lib/api/quotations';
  import { getErrorMessage } from '$lib/api/client';
  import { formatMoney, formatPercent } from '$lib/utils/money';
  import { QUOTE_VERTICAL_META } from '$lib/types';
  import { session } from '$lib/state/session.svelte';
  import type { Quotation } from '$lib/types';
  import Pagination from '$lib/components/ui/Pagination.svelte';

  const {
    search,
    vertical,
    designerLabel,
    onOpen
  }: {
    search: string;
    vertical: string;
    designerLabel: (q: Quotation) => string;
    onOpen: (id: number) => void;
  } = $props();

  const PER_PAGE = 30;
  let page = $state(1);

  // Reset to page 1 whenever the shared search/vertical filters change —
  // otherwise a filter change could leave the view on a now-out-of-range
  // page showing "no results" instead of the filtered first page.
  $effect(() => {
    void search;
    void vertical;
    page = 1;
  });

  const query = createQuery(() => ({
    queryKey: ['approved-quotations', vertical, search, page],
    queryFn: () =>
      quotationsApi.list({
        status: 'approved',
        vertical: vertical === 'all' ? undefined : vertical,
        q: search || undefined,
        page,
        per_page: PER_PAGE
      })
  }));

  // The 40% standard is retained as a reporting threshold. The authenticated
  // member's edit/approval allowance can be higher and is shown separately.
  const STANDARD_GIVEAWAY_BPS = 4000;
  const giveawayAllowanceBps = $derived(session.me?.user.giveaway_cap_bps ?? STANDARD_GIVEAWAY_BPS);

  type SortKey =
    | 'quotation_number'
    | 'client_name'
    | 'project_name'
    | 'grand_total_minor'
    | 'combined_given_bps'
    | 'approved_at';
  let sortKey = $state<SortKey>('approved_at');
  let sortDir = $state<'asc' | 'desc'>('desc');

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      sortDir = sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      sortKey = key;
      sortDir = 'asc';
    }
  }

  // Sorts only the currently-loaded page — the server already does the real
  // filtering/pagination (status/vertical/q), a client-side full-dataset
  // sort would need a new server sort param for one table. If cross-page
  // sorting is ever needed, add `sort=`/`order=` to listQuotations and drop
  // this for a server-driven sort instead.
  const rows = $derived.by(() => {
    const list = [...(query.data?.results ?? [])];
    list.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const an = typeof av === 'string' ? av.toLowerCase() : (av ?? 0);
      const bn = typeof bv === 'string' ? bv.toLowerCase() : (bv ?? 0);
      if (an < bn) return sortDir === 'asc' ? -1 : 1;
      if (an > bn) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  });

  function sortIcon(key: SortKey) {
    if (sortKey !== key) return null;
    return sortDir === 'asc' ? ChevronUp : ChevronDown;
  }
</script>

<div class="flex-1 overflow-y-auto px-4 lg:px-7 pb-6">
  <p class="mb-3 text-xs text-dark-gray">
    {#if session.isAdmin}
      Standard giveaway reporting threshold: {formatPercent(STANDARD_GIVEAWAY_BPS)}. Admins are exempt from user allowances.
    {:else}
      Your edit and approval allowance: {formatPercent(giveawayAllowanceBps)} (hotel quotations are exempt). Given flags use the standard {formatPercent(STANDARD_GIVEAWAY_BPS)} reporting threshold.
    {/if}
  </p>
  {#if query.isLoading}
    <p class="tym-loading-state">Loading approved quotations&hellip;</p>
  {:else if query.isError}
    <p class="tym-error-state">{getErrorMessage(query.error)}</p>
  {:else}
    <div class="tym-table-wrap tym-table-scroll">
      <table class="tym-table">
        <thead>
          <tr>
            <th>
              <button type="button" class="flex items-center gap-1" onclick={() => toggleSort('quotation_number')}>
                Number
                {#if sortIcon('quotation_number')}
                  {@const Icon = sortIcon('quotation_number')}
                  <Icon class="h-3 w-3" />
                {/if}
              </button>
            </th>
            <th>
              <button type="button" class="flex items-center gap-1" onclick={() => toggleSort('client_name')}>
                Client
                {#if sortIcon('client_name')}
                  {@const Icon = sortIcon('client_name')}
                  <Icon class="h-3 w-3" />
                {/if}
              </button>
            </th>
            <th>
              <button type="button" class="flex items-center gap-1" onclick={() => toggleSort('project_name')}>
                Project
                {#if sortIcon('project_name')}
                  {@const Icon = sortIcon('project_name')}
                  <Icon class="h-3 w-3" />
                {/if}
              </button>
            </th>
            <th>Lot</th>
            <th>Vertical</th>
            <th class="num">
              <button type="button" class="ml-auto flex items-center gap-1" onclick={() => toggleSort('grand_total_minor')}>
                Grand total
                {#if sortIcon('grand_total_minor')}
                  {@const Icon = sortIcon('grand_total_minor')}
                  <Icon class="h-3 w-3" />
                {/if}
              </button>
            </th>
            <!-- Internal margin giveaway: customer discount + partner
                  commission as a share of list price. Flags use the standard
                  40% reporting threshold, not a user's allowance. -->
             <th class="num" title="Customer discount + partner commission; flagged over the standard 40% reporting threshold">
              <button type="button" class="ml-auto flex items-center gap-1" onclick={() => toggleSort('combined_given_bps')}>
                Given
                {#if sortIcon('combined_given_bps')}
                  {@const Icon = sortIcon('combined_given_bps')}
                  <Icon class="h-3 w-3" />
                {/if}
              </button>
            </th>
            <th>
              <button type="button" class="flex items-center gap-1" onclick={() => toggleSort('approved_at')}>
                Approved
                {#if sortIcon('approved_at')}
                  {@const Icon = sortIcon('approved_at')}
                  <Icon class="h-3 w-3" />
                {/if}
              </button>
            </th>
            <th>Designer</th>
          </tr>
        </thead>
        <tbody>
          {#each rows as q (q.id)}
            <tr class="clickable" onclick={() => onOpen(q.id)}>
              <td class="font-mono text-[12.5px]">{q.quotation_number}</td>
              <td class="font-medium text-navy">{q.client_name || 'Untitled client'}</td>
              <td class="muted">{q.project_name || 'Untitled project'}</td>
              <td class="muted whitespace-nowrap">{q.lot_label}</td>
              <td>
                <span class="tym-badge text-toyama-teal bg-toyama-teal/10">
                  {QUOTE_VERTICAL_META[q.vertical]?.label ?? q.vertical}
                </span>
              </td>
              <td class="num font-semibold text-navy">{formatMoney(q.grand_total_minor, q.currency)}</td>
              <!-- Over the pool: a red number alone is skimmable, so flag it.
                   The figure is the server's (true list subtotal, correct at
                   any section quantity) — never recomputed here. -->
              <td class="num">
                {#if q.combined_given_bps > STANDARD_GIVEAWAY_BPS}
                  <span
                    class="inline-flex items-center gap-1 rounded bg-red-600 px-1.5 py-0.5 font-semibold text-white"
                    title="Over the standard 40.00% giveaway reporting threshold"
                  >
                    <TriangleAlert class="h-3 w-3" aria-hidden="true" />
                    {formatPercent(q.combined_given_bps)}
                  </span>
                {:else}
                  <span class="muted">{formatPercent(q.combined_given_bps)}</span>
                {/if}
              </td>
              <td class="muted">{q.approved_at ? new Date(q.approved_at).toLocaleDateString() : '—'}</td>
              <td class="muted">{designerLabel(q)}</td>
            </tr>
          {:else}
            <tr><td class="empty" colspan="9">No approved quotations{search ? ' match your search' : ''}.</td></tr>
          {/each}
        </tbody>
      </table>
    </div>
    <Pagination page={query.data?.page ?? page} perPage={PER_PAGE} count={query.data?.count ?? 0} onChange={(p) => (page = p)} />
  {/if}
</div>
